"use client";

import { CreditCard, Smartphone } from "lucide-react";
import { useTranslations } from "next-intl";
import type { PaymentMethod } from "@/lib/payment/types";

interface PaymentMethodSelectorProps {
  methods: PaymentMethod[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  loading?: boolean;
  /** Override helper text under the label */
  hint?: string;
}

export function PaymentMethodSelector({
  methods,
  selectedId,
  onSelect,
  loading = false,
  hint,
}: PaymentMethodSelectorProps) {
  const t = useTranslations("Subscribe");
  const resolvedHint = hint ?? t("defaultPaymentHint");

  if (loading) {
    return (
      <div className="space-y-3">
        <label className="block text-sm font-medium text-slate-300">
          {t("paymentMethodLabel")}
        </label>
        <div className="h-24 rounded-lg bg-slate-800/30 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-slate-300">
        {t("paymentMethodLabel")}
        {resolvedHint ? (
          <span className="ml-2 text-slate-500 font-normal">{resolvedHint}</span>
        ) : null}
      </label>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {methods.map((method) => {
          const isSelected = selectedId === method.id;
          const Icon = method.id === "alipay" ? Smartphone : CreditCard;
          return (
            <button
              key={method.id}
              type="button"
              onClick={() => onSelect(method.id)}
              className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all relative hover:scale-[1.02] active:scale-[0.98] ${
                isSelected
                  ? "border-teal-primary bg-teal-primary/10"
                  : "border-slate-700 hover:border-slate-600 bg-slate-800/30"
              }`}
            >
              {method.recommended && (
                <span className="absolute -top-2 right-2 px-2 py-0.5 text-[10px] font-semibold text-teal-light bg-teal-primary/30 rounded-full">
                  {t("recommended")}
                </span>
              )}
              <Icon className="w-6 h-6 text-slate-400" />
              <span className="text-sm font-medium text-slate-200">
                {method.name}
              </span>
              {method.region && (
                <span className="text-xs text-slate-500">{method.region}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
